<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { i18n } from '@/i18n';
import MultipleSelectInput from '@/corePagesModule/SelectInput/MultipleSelectInput.vue';
import { useGroupListMultipleSelectInputStore } from './useGroupListMultipleSelectInputStore';

const props = withDefaults(
  defineProps<{
    selectInputIndex: number;
    storeTag: string;
    selectedGroupIdsProp?: string[];
    showLabel?: boolean;
    useReset?: boolean;
    reload?: boolean;
    useTeleport?: boolean;
  }>(),
  {
    /**
     * propOption初期化 defaultでArray, Objectを指定する場合ファクトリー関数を用いる必要がある
     */
    selectedGroupIdsProp: () => [] as string[],
    useReset: true,
  }
);

const emits = defineEmits<{
  (e: 'selectedGroups', groupIds: string[]): void;
}>();

const t = i18n.t;

const store = useGroupListMultipleSelectInputStore(props.storeTag)();
const { state } = storeToRefs(store);

onMounted(() => {
  store.init();
  if (props.selectedGroupIdsProp) {
    state.value.selectedGroupIds = props.selectedGroupIdsProp;
  }
});

watch(
  () => props.selectedGroupIdsProp,
  (newVal) => {
    if (newVal !== undefined) {
      state.value.selectedGroupIds = newVal;
    }
  }
);

watch(
  () => props.reload,
  async (newVal) => {
    if (newVal) {
      await store.reloadGroupData();
    }
  }
);

/**
 * グループ名を選択した際の処理
 * @param indexes - 選択したグループのインデックス
 */
const onSelectedGroup = (indexes: number[]) => {
  store.onSelectedGroup(indexes);
};

/**
 * 外すボタンを押した際の処理
 * @param value - 選択したグループ名
 */
const onUpdatedValue = (value: string[]) => {
  if (value.length === 0) {
    store.onSelectedDefaultValue();
  }
};

const useLabel = computed(() => (props.showLabel ? t('グループ') : ''));

/**
 * セレクトボックスをクリックした際の処理
 */
const onClickSelectInput = async () => {
  await store.reloadGroupData();
  state.value.selectInputState = true;
};

/**
 * セレクトボックスを閉じた際の処理
 */
const onClose = () => {
  state.value.selectInputState = false;
  emits('selectedGroups', state.value.selectedGroupIds);
};
</script>

<template>
  <MultipleSelectInput
    :index="selectInputIndex"
    :selected-list="store.selectedGroupNames"
    :placeholder="t('グループ名')"
    :label="useLabel"
    :filter-type="'AGGREGATE_BUSINESS_GROUP'"
    :use-reset="useReset"
    :is-open-prop="state.selectInputState"
    :prop-options="store.groupListNames"
    :prop-paging-state="state.pagingState"
    :prop-paging-options="state.pagingOptions"
    :prop-selected-indexes="store.selectedIndexes"
    :use-teleport="useTeleport"
    @click:select-input="() => onClickSelectInput()"
    @close="() => onClose()"
    @update:model-value="(value) => onUpdatedValue(value)"
    @update:value-with-indexes="(_, indexes) => onSelectedGroup(indexes)"
  />
</template>
